import { $api } from '~/utils/api'
import { logApiError } from '~/utils/logger'
import { buildQueryString } from '~/utils/queryParams'

export interface BlogPost {
  id: string
  title: string
  slug: string
  excerpt: string | null
  content: string
  featured_image_url: string | null
  category: string | null
  status: 'draft' | 'published' | 'archived'
  published_at: string | null
  display_order: number
  meta_title: string | null
  meta_description: string | null
  created_at: string | null
  created_by: string | null
  updated_at: string | null
  updated_by: string | null
}

export interface BlogPostInput {
  title: string
  slug?: string
  excerpt?: string | null
  content: string
  featured_image_url?: string | null
  category?: string | null
  status?: 'draft' | 'published' | 'archived'
  published_at?: string | null
  display_order?: number
  meta_title?: string | null
  meta_description?: string | null
}

interface BlogFilters {
  page?: number
  limit?: number
  search?: string
  category?: string
  status?: 'draft' | 'published' | 'archived'
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  publicOnly?: boolean
}

interface BlogListResponse {
  data: BlogPost[]
  metadata: {
    total: number
    page: number
    limit: number
    totalPages: number
  }
}

export const useBlog = () => {
  /**
   * Get all blog posts (admin view - includes drafts/archived)
   */
  const getBlogPosts = async (filters: BlogFilters = {}): Promise<BlogListResponse> => {
    try {
      const queryString = buildQueryString(filters)

      return await $api(`/api/blog${queryString ? `?${queryString}` : ''}`)
    }
    catch (error) {
      logApiError('/api/blog', error, { filters })
      throw error
    }
  }

  /**
   * Get published blog posts only (public view)
   */
  const getPublishedPosts = async (filters: Omit<BlogFilters, 'status'> = {}): Promise<BlogListResponse> => {
    return getBlogPosts({ ...filters, publicOnly: true })
  }

  /**
   * Get a single blog post by ID
   */
  const getBlogPost = async (id: string): Promise<BlogPost> => {
    try {
      return await $api(`/api/blog/${id}`)
    }
    catch (error) {
      logApiError(`/api/blog/${id}`, error)
      throw error
    }
  }

  /**
   * Get a single blog post by slug
   */
  const getBlogPostBySlug = async (slug: string): Promise<BlogPost> => {
    try {
      return await $api(`/api/blog/slug/${slug}`)
    }
    catch (error) {
      logApiError(`/api/blog/slug/${slug}`, error)
      throw error
    }
  }

  /**
   * Create a new blog post
   */
  const createBlogPost = async (data: BlogPostInput): Promise<BlogPost> => {
    try {
      return await $api('/api/blog', {
        method: 'POST',
        body: data,
      })
    }
    catch (error) {
      logApiError('/api/blog', error, { data })
      throw error
    }
  }

  /**
   * Update a blog post
   */
  const updateBlogPost = async (id: string, data: Partial<BlogPostInput>): Promise<BlogPost> => {
    try {
      return await $api(`/api/blog/${id}`, {
        method: 'PATCH',
        body: data,
      })
    }
    catch (error) {
      logApiError(`/api/blog/${id}`, error, { data })
      throw error
    }
  }

  /**
   * Delete a blog post
   */
  const deleteBlogPost = async (id: string): Promise<{ success: boolean }> => {
    try {
      return await $api(`/api/blog/${id}`, {
        method: 'DELETE',
      })
    }
    catch (error) {
      logApiError(`/api/blog/${id}`, error)
      throw error
    }
  }

  /**
   * Update blog post status
   */
  const updateBlogPostStatus = async (id: string, status: 'draft' | 'published' | 'archived'): Promise<BlogPost> => {
    return updateBlogPost(id, { status })
  }

  /**
   * Publish a blog post
   */
  const publishBlogPost = async (id: string): Promise<BlogPost> => {
    return updateBlogPostStatus(id, 'published')
  }

  /**
   * Archive a blog post
   */
  const archiveBlogPost = async (id: string): Promise<BlogPost> => {
    return updateBlogPostStatus(id, 'archived')
  }

  /**
   * Set blog post to draft
   */
  const draftBlogPost = async (id: string): Promise<BlogPost> => {
    return updateBlogPostStatus(id, 'draft')
  }

  return {
    getBlogPosts,
    getPublishedPosts,
    getBlogPost,
    getBlogPostBySlug,
    createBlogPost,
    updateBlogPost,
    deleteBlogPost,
    updateBlogPostStatus,
    publishBlogPost,
    archiveBlogPost,
    draftBlogPost,
  }
}
